/**
 * components/shared/CoordConversorModal.tsx
 * Modal React del conversor de coordenadas UTM30.
 *
 * Porta la lógica verificada (15/15 PASS, commit 82d2517e) de
 * public/guia-calidad-alt-c.html a TypeScript.
 *
 * Pipeline de detección (orden exacto):
 *   1.  Guard Plus Code
 *   1b. UTM zona explícita (29N/30N/31N) ← FIX BUG-CONV-02
 *   2.  UTM coma sobre raw  ← Fix C2/C3 (ANTES de normalizar)
 *   3.  Normalización decimal europea (coma → punto)
 *   4.  UTM espacio / punto y coma / prefijo X:Y: ← FIX BUG-CONV-01
 *   5.  DD con sufijo hemisferio (36.8°N, 2.4°W) ← FIX BUG-CONV-03
 *   6.  DD (grados decimales) — sin restricción geográfica
 *   7.  DMS (grados-minutos-segundos)
 *   8.  Error genérico
 *
 * @version 1.0.0
 */
import type { FC, KeyboardEvent } from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';

// ============================================
// TYPES
// ============================================

type ConversorResult =
  | { kind: 'utm';   x: number; y: number }
  | { kind: 'plus';  message: string }
  | { kind: 'error'; message: string };

export interface CoordConversorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================
// CONSTANTES DE EJEMPLO
// ============================================

const CHIPS = [
  { label: 'DD coma',          value: '37.17654, -3.59769' },
  { label: 'DD espacio',       value: '37.17654 -3.59769' },
  { label: 'DD europeo',       value: '37,17654 -3,59769' },
  { label: 'DMS N/W',          value: "37°10'35.5\"N 3°35'51.7\"W" },
  { label: 'DMS N/O',          value: "37°10'35.5\"N 3°35'51.7\"O" },
  { label: 'UTM espacio',      value: '446943 4114624' },
  { label: 'UTM coma',         value: '446943,4114624' },
  { label: 'UTM dec. europeo', value: '446943,23 4114624,50' },
  { label: 'Sevilla DD',       value: '37.3826, -5.9963' },
  { label: 'ED50 UTM30',       value: '447071.45 4114836.12' },
] as const;

// ============================================
// FUNCIONES AUXILIARES (módulo interno)
// ============================================

/** Convierte un token DMS ("37°10'35.5\"N") a grados decimales. */
function parseDMS(s: string): number | null {
  // Normaliza variantes: grado °, minuto ', segundo " o ''
  const clean = s
    .replace(/[°º]/g, 'd')
    .replace(/['']/g, 'm')
    .replace(/["""]/g, 's')
    .trim();

  const m = clean.match(
    /^(\d{1,3})d(\d{1,2})m([\d.]+)s([NSEWOnsewо])?$/i,
  );
  if (!m) return null;

  const deg = parseFloat(m[1]);
  const min = parseFloat(m[2]);
  const sec = parseFloat(m[3]);
  const hem = (m[4] ?? '').toUpperCase();

  if (min >= 60 || sec >= 60) return null;
  let dd = deg + min / 60 + sec / 3600;
  if (hem === 'S' || hem === 'W' || hem === 'O') dd = -dd;
  return dd;
}

/** Convierte lat/lon WGS84 a UTM30 ETRS89 (EPSG:25830). */
function latLonToUTM30(lat: number, lon: number): { x: number; y: number } {
  const a  = 6378137.0;
  const f  = 1 / 298.257223563;
  const b  = a * (1 - f);
  const e2 = 1 - (b * b) / (a * a);
  const k0 = 0.9996;
  const lon0 = ((30 - 1) * 6 - 180 + 3) * (Math.PI / 180); // zona 30

  const latR = lat * (Math.PI / 180);
  const lonR = lon * (Math.PI / 180);

  const N  = a / Math.sqrt(1 - e2 * Math.sin(latR) ** 2);
  const T  = Math.tan(latR) ** 2;
  const C  = (e2 / (1 - e2)) * Math.cos(latR) ** 2;
  const A  = Math.cos(latR) * (lonR - lon0);

  const e4 = e2 * e2;
  const e6 = e4 * e2;
  const M  = a * (
    (1 - e2 / 4 - 3 * e4 / 64 - 5 * e6 / 256) * latR
    - (3 * e2 / 8 + 3 * e4 / 32 + 45 * e6 / 1024) * Math.sin(2 * latR)
    + (15 * e4 / 256 + 45 * e6 / 1024) * Math.sin(4 * latR)
    - (35 * e6 / 3072) * Math.sin(6 * latR)
  );

  const x = k0 * N * (
    A + (1 - T + C) * A ** 3 / 6
    + (5 - 18 * T + T * T + 72 * C - 58 * (e2 / (1 - e2))) * A ** 5 / 120
  ) + 500000;

  const y = k0 * (
    M + N * Math.tan(latR) * (
      A * A / 2
      + (5 - T + 9 * C + 4 * C * C) * A ** 4 / 24
      + (61 - 58 * T + T * T + 600 * C - 330 * (e2 / (1 - e2))) * A ** 6 / 720
    )
  );

  return { x: Math.round(x), y: Math.round(y) };
}

/**
 * Reproyecta coordenadas UTM de zona 29 o 31 a zona 30 (EPSG:25830).
 * Aproximación válida para Andalucía (error < 1 m para zona 29).
 */
function utmZonaALatLon(
  x: number, y: number, zona: number,
): { lat: number; lon: number } {
  const a  = 6378137.0;
  const f  = 1 / 298.257223563;
  const b  = a * (1 - f);
  const e2 = 1 - (b * b) / (a * a);
  const k0 = 0.9996;
  const lon0 = ((zona - 1) * 6 - 180 + 3) * (Math.PI / 180);
  const e1  = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));
  const M   = y / k0;
  const mu  = M / (a * (1 - e2 / 4 - 3 * e2 ** 2 / 64 - 5 * e2 ** 3 / 256));
  const phi1 = mu
    + (3 * e1 / 2 - 27 * e1 ** 3 / 32) * Math.sin(2 * mu)
    + (21 * e1 ** 2 / 16 - 55 * e1 ** 4 / 32) * Math.sin(4 * mu)
    + (151 * e1 ** 3 / 96) * Math.sin(6 * mu);
  const N1 = a / Math.sqrt(1 - e2 * Math.sin(phi1) ** 2);
  const T1 = Math.tan(phi1) ** 2;
  const C1 = (e2 / (1 - e2)) * Math.cos(phi1) ** 2;
  const R1 = a * (1 - e2) / (1 - e2 * Math.sin(phi1) ** 2) ** 1.5;
  const D  = (x - 500000) / (N1 * k0);
  const lat = phi1 - (N1 * Math.tan(phi1) / R1) * (
    D ** 2 / 2
    - (5 + 3 * T1 + 10 * C1 - 4 * C1 ** 2 - 9 * (e2 / (1 - e2))) * D ** 4 / 24
    + (61 + 90 * T1 + 298 * C1 + 45 * T1 ** 2 - 252 * (e2 / (1 - e2)) - 3 * C1 ** 2) * D ** 6 / 720
  );
  const lon = lon0 + (
    D
    - (1 + 2 * T1 + C1) * D ** 3 / 6
    + (5 - 2 * C1 + 28 * T1 - 3 * C1 ** 2 + 8 * (e2 / (1 - e2)) + 24 * T1 ** 2) * D ** 5 / 120
  ) / Math.cos(phi1);
  return {
    lat: lat * (180 / Math.PI),
    lon: lon * (180 / Math.PI),
  };
}

/** Pipeline de conversión principal (orden exacto del arranque). */
function convertir(raw: string): ConversorResult {
  const trimmed = raw.trim();

  // 1. Guard Plus Code
  if (/^[23456789CFGHJMPQRVWX]{2,}[+]/.test(trimmed)) {
    return {
      kind: 'plus',
      message: 'Los Plus Codes no son compatibles. Conviértelo primero a DD o UTM30 en Google Maps.',
    };
  }

  // 1b. UTM con zona explícita: "29N 663000 4126000" / "30N 446943 4114624"
  const utmZona = trimmed.match(/^(29|30|31)[NS]?\s+(\d{5,7})\s+(\d{7})$/i);
  if (utmZona) {
    const zona = parseInt(utmZona[1], 10);
    const x    = parseInt(utmZona[2], 10);
    const y    = parseInt(utmZona[3], 10);
    if (zona === 30) {
      if (x >= 50000 && x <= 800000 && y >= 4000000 && y <= 4900000) {
        return { kind: 'utm', x, y };
      }
    } else {
      // Reproyectar zona 29 o 31 → lat/lon → UTM30
      const { lat, lon } = utmZonaALatLon(x, y, zona);
      return { kind: 'utm', ...latLonToUTM30(lat, lon) };
    }
  }

  // 2. UTM coma sobre raw (Fix C2/C3 — ANTES de normalizar decimales)
  const utmComa = trimmed.match(/^(\d{5,7})[,](\d{7})$/);
  if (utmComa) {
    const x = parseInt(utmComa[1], 10);
    const y = parseInt(utmComa[2], 10);
    if (x >= 50000 && x <= 800000 && y >= 4000000 && y <= 4900000) {
      return { kind: 'utm', x, y };
    }
  }

  // 3. Normalización: reemplaza coma decimal europea por punto
  //    Solo cuando la coma separa dígitos (no actúa sobre separador de par)
  const rawN = trimmed.replace(/(\d),(\d)/g, '$1.$2');

  // 4. UTM espacio / punto y coma — con o sin prefijo "X:" / "Y:"
  //    Acepta: "446943 4114624", "X: 446943 Y: 4113200", "446943; 4114624"
  const rawU = rawN.replace(/[Xx]\s*:\s*/g, '').replace(/[Yy]\s*:\s*/g, '');
  const utmEsp = rawU.match(/^(\d{5,7}(?:\.\d+)?)\s*[;\s]\s*(\d{7}(?:\.\d+)?)$/);
  if (utmEsp) {
    const x = parseFloat(utmEsp[1]);
    const y = parseFloat(utmEsp[2]);
    if (x >= 50000 && x <= 800000 && y >= 4000000 && y <= 4900000) {
      return { kind: 'utm', x: Math.round(x), y: Math.round(y) };
    }
  }

  // 5. Grados decimales con sufijo hemisferio: "36.8340°N, 2.4637°W"
  //    Acepta °N/°S y °E/°W/°O — punto o coma como separador de par
  const ddHem = rawN.match(
    /^(-?\d{1,3}\.?\d*)\s*°?\s*([NSns])\s*[,\s]\s*(-?\d{1,3}\.?\d*)\s*°?\s*([EWOewo])$/,
  );
  if (ddHem) {
    let lat = parseFloat(ddHem[1]);
    let lon = parseFloat(ddHem[3]);
    if (/^[Ss]$/.test(ddHem[2])) lat = -Math.abs(lat);
    if (/^[WwOo]$/.test(ddHem[4])) lon = -Math.abs(lon);
    if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
      return { kind: 'utm', ...latLonToUTM30(lat, lon) };
    }
  }

  // 6. Grados decimales (DD) — formato numérico puro
  const dd = rawN.match(/^(-?\d{1,3}\.?\d*)\s*[,;\s]\s*(-?\d{1,3}\.?\d*)$/);
  if (dd) {
    const a = parseFloat(dd[1]);
    const b = parseFloat(dd[2]);
    const lat = (a >= -90  && a <= 90)  ? a : b;
    const lon = (b >= -180 && b <= 180) ? b : a;
    if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180
        && (Math.abs(lat) > 0.5 || Math.abs(lon) > 0.5)) {
      return { kind: 'utm', ...latLonToUTM30(lat, lon) };
    }
  }

  // 7. DMS (grados-minutos-segundos)
  const tokens = trimmed.split(/\s+(?=[NEWS0-9])/i);
  if (tokens.length === 2) {
    const lat = parseDMS(tokens[0]);
    const lon = parseDMS(tokens[1]);
    if (lat !== null && lon !== null
        && lat >= 35 && lat <= 44
        && lon >= -10 && lon <= 5) {
      return { kind: 'utm', ...latLonToUTM30(lat, lon) };
    }
  }

  // 7. Error genérico
  return {
    kind: 'error',
    message: 'Formato no reconocido. Prueba con DD, DMS o UTM30.',
  };
}

// ============================================
// COMPONENT
// ============================================

export const CoordConversorModal: FC<CoordConversorModalProps> = ({ isOpen, onClose }) => {
  const [input,         setInput]         = useState('');
  const [result,        setResult]        = useState<ConversorResult | null>(null);
  const [copiedX,       setCopiedX]       = useState(false);
  const [copiedY,       setCopiedY]       = useState(false);
  const [copiedXY,      setCopiedXY]      = useState(false);
  const [toastMsg,      setToastMsg]      = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Foco automático al abrir; ESC cierra
  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => inputRef.current?.focus(), 50);

    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Limpiar estado al cerrar
  useEffect(() => {
    if (!isOpen) { setInput(''); setResult(null); setToastMsg(null); }
  }, [isOpen]);

  const handleConvert = useCallback(() => {
    if (!input.trim()) return;
    setResult(convertir(input));
  }, [input]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleConvert();
  };

  const copy = (text: string, which: 'x' | 'y' | 'xy') => {
    void navigator.clipboard.writeText(text).then(() => {
      if (which === 'x')      { setCopiedX(true);  setTimeout(() => setCopiedX(false),  1500); }
      else if (which === 'y') { setCopiedY(true);  setTimeout(() => setCopiedY(false),  1500); }
      else                    { setCopiedXY(true); setTimeout(() => setCopiedXY(false), 1500); }
    });
  };

  const copyFormat = (label: string, text: string) => {
    void navigator.clipboard.writeText(text).then(() => {
      setToastMsg(`Copiado: ${label}`);
      setTimeout(() => setToastMsg(null), 1800);
    });
  };

  if (!isOpen) return null;

  const utm = result?.kind === 'utm' ? result : null;
  const err = result?.kind === 'error' || result?.kind === 'plus' ? result.message : null;

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Conversor de coordenadas UTM30"
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 560, width: '100%',
          background: 'var(--bg-2, #18181b)',
          border: '1px solid var(--border, #27272a)',
          borderRadius: 'var(--radius-xl, 16px)',
          padding: '32px',
          position: 'relative',
          animation: 'modalIn .2s ease-out',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* ── Cabecera ── */}
        <button
          onClick={onClose}
          type="button"
          aria-label="Cerrar"
          style={{
            position: 'absolute', top: 12, right: 12,
            width: 32, height: 32,
            background: '#3f3f46', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6,
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" width="18" height="18">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56, margin: '0 auto 16px',
            background: 'rgba(45,212,191,.10)',
            borderRadius: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {/* Icono: dos flechas bidireccionales — representa conversión entre sistemas de coordenadas */}
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--teal, #2dd4bf)"
              strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
              width="26" height="26" aria-hidden="true">
              <path d="M3 8h18M3 8l3-3M3 8l3 3"/>
              <path d="M21 16H3M21 16l-3-3M21 16l-3 3"/>
            </svg>
          </div>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.25rem', fontWeight: 700, margin: '0 0 8px' }}>
            Conversor de coordenadas
          </h2>
          <p style={{ margin: 0, fontSize: '.9rem', color: 'var(--fg-2, #a1a1aa)', fontFamily: "'Space Grotesk', sans-serif" }}>
            DD · DMS · UTM30 → <strong>ETRS89 UTM zona 30N (EPSG:25830)</strong>
          </p>
        </div>

        {/* ── Chips de ejemplo ── */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 0 12px',
          fontFamily: "'Space Grotesk', sans-serif",
        }}>
          {CHIPS.map(({ label, value }) => (
            <button
              key={label}
              type="button"
              onClick={() => { setInput(value); setResult(null); }}
              style={{
                padding: '3px 10px', borderRadius: 12, fontSize: 12,
                background: 'transparent',
                border: '1px solid var(--border, #3f3f46)',
                color: 'var(--fg-3, #71717a)', cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'border-color .15s, color .15s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = 'var(--teal, #2dd4bf)';
                e.currentTarget.style.color = 'var(--teal, #2dd4bf)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'var(--border, #3f3f46)';
                e.currentTarget.style.color = 'var(--fg-3, #71717a)';
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Input + botón ── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => { setInput(e.target.value); setResult(null); }}
            onKeyDown={handleKeyDown}
            placeholder="37.17654, -3.59769 — o DMS — o UTM30"
            aria-label="Coordenadas a convertir"
            style={{
              flex: 1, padding: '9px 12px', borderRadius: 'var(--r, 8px)',
              border: `1px solid ${err ? 'var(--error, #f87171)' : 'var(--border, #3f3f46)'}`,
              background: 'var(--surface2, #18181b)', color: 'var(--fg, #fafafa)',
              fontFamily: "'JetBrains Mono', monospace", fontSize: 13,
              outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={handleConvert}
            disabled={!input.trim()}
            onMouseOver={(e) => { e.currentTarget.style.opacity = '0.85'; }}
            onMouseOut={(e)  => { e.currentTarget.style.opacity = input.trim() ? '1' : '0.45'; }}
            style={{
              padding: '9px 16px', borderRadius: 'var(--r, 8px)',
              background: 'var(--teal, #2dd4bf)', color: '#0f172a',
              border: 'none', fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Space Grotesk', sans-serif", fontSize: 13,
              opacity: input.trim() ? 1 : 0.45,
              transition: 'opacity .15s',
            }}
          >
            Convertir a UTM30
          </button>
        </div>

        {/* ── Error inline ── */}
        {err && (
          <p role="alert" style={{
            margin: '0 0 12px', fontSize: 12, color: 'var(--error, #f87171)',
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            {err}
          </p>
        )}

        {/* ── Resultado UTM ── */}
        {utm && (
          <>
            {/* Aviso fuera de Andalucía */}
            {(utm.y > 4_250_000 || utm.y < 4_000_000) && (
              <p role="alert" style={{
                margin: '0 0 10px', fontSize: 12,
                color: 'var(--warning, #fb923c)',
                fontFamily: "'Space Grotesk', sans-serif",
              }}>
                Aviso: la coordenada Y ({utm.y.toLocaleString('es-ES')}) está fuera del rango esperado para Andalucía (4 000 000 – 4 250 000). Verifica el origen de las coordenadas.
              </p>
            )}

            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, margin: '4px 0 8px',
            }}>
              {([
                { label: 'X — Este',  value: utm.x, which: 'x' as const, copied: copiedX },
                { label: 'Y — Norte', value: utm.y, which: 'y' as const, copied: copiedY },
              ] as const).map(({ label, value, which, copied }) => (
                <div key={which} style={{
                  background: 'var(--teal-bg, rgba(45,212,191,.08))',
                  border: '1px solid var(--teal-border, rgba(45,212,191,.22))',
                  borderRadius: 'var(--r2, 6px)', padding: '10px 12px',
                }}>
                  <span style={{
                    display: 'block', fontSize: 11,
                    color: 'var(--fg3, #71717a)',
                    fontFamily: "'Space Grotesk', sans-serif",
                    marginBottom: 4,
                  }}>
                    {label}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 16, fontWeight: 700,
                      color: 'var(--teal, #2dd4bf)', letterSpacing: '.02em',
                    }}>
                      {value.toLocaleString('es-ES')}
                    </span>
                    <button
                      type="button"
                      onClick={() => copy(String(value), which)}
                      aria-label={`Copiar ${label}`}
                      title={`Copiar ${label}`}
                      style={{
                        marginLeft: 'auto', padding: '3px 6px',
                        borderRadius: 4, border: '1px solid var(--teal-border, rgba(45,212,191,.3))',
                        background: 'transparent',
                        color: copied ? 'var(--teal, #2dd4bf)' : 'var(--fg3, #71717a)',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
                        transition: 'color .15s',
                      }}
                    >
                      {copied
                        ? <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l3 3 7-7"/></svg>
                        : <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" width="13" height="13">
                            <rect x="5" y="2" width="9" height="10" rx="1.5"/>
                            <path d="M2 5H1.5A1.5 1.5 0 000 6.5v8A1.5 1.5 0 001.5 16h8A1.5 1.5 0 0011 14.5V14" strokeLinecap="round"/>
                          </svg>
                      }
                      <span style={{ fontSize: 11, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}>
                        {which.toUpperCase()}
                      </span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Botón «Copiar X, Y» — par listo para pegar en QGIS/CSV */}
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => copy(`${utm.x}, ${utm.y}`, 'xy')}
                aria-label="Copiar X e Y juntos"
                style={{
                  padding: '5px 14px', fontSize: 12, borderRadius: 6,
                  border: '1px solid var(--teal-border, rgba(45,212,191,.3))',
                  background: 'transparent',
                  color: copiedXY ? 'var(--teal, #2dd4bf)' : 'var(--fg3, #71717a)',
                  cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
                  transition: 'color .15s',
                }}
              >
                {copiedXY ? '✓ Copiado' : 'Copiar X, Y'}
              </button>
            </div>
          </>
        )}

        {/* ── Zona «Copiar como…» ── */}
        {utm && (() => {
          const X = utm.x;
          const Y = utm.y;
          const Xd = String(X).replace('.', ',');
          const Yd = String(Y).replace('.', ',');
          const formats = [
            { id: 'qgis',    label: 'X Y  QGIS / gvSIG',         value: `${X} ${Y}` },
            { id: 'csv',     label: 'X,Y  CSV / Excel',           value: `${X},${Y}` },
            { id: 'lo',      label: 'X;Y  LibreOffice',           value: `${X};${Y}` },
            { id: 'eu',      label: 'X,Y  decimal europeo',       value: `${Xd};${Yd}` },
            { id: 'geojson', label: 'GeoJSON coordinates',        value: `[${X}, ${Y}]` },
            { id: 'wkt',     label: 'WKT POINT  PostGIS',         value: `POINT(${X} ${Y})` },
          ];
          return (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                borderTop: '1px solid var(--border, #27272a)',
                margin: '0 0 10px',
                paddingTop: 12,
                fontSize: 11,
                color: 'var(--fg3, #71717a)',
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
              }}>
                Copiar como…
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {formats.map(({ id, label, value }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => copyFormat(label, value)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '6px 10px', borderRadius: 6,
                      border: '1px solid var(--border, #3f3f46)',
                      background: 'transparent', cursor: 'pointer',
                      fontFamily: "'Space Grotesk', sans-serif",
                      transition: 'border-color .15s, background .15s',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(45,212,191,.4)';
                      e.currentTarget.style.background = 'rgba(45,212,191,.06)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border, #3f3f46)';
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span style={{ fontSize: 11, color: 'var(--fg3, #71717a)' }}>{label}</span>
                    <span style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 11, color: 'var(--fg, #fafafa)',
                      background: 'rgba(255,255,255,.05)', borderRadius: 4,
                      padding: '1px 6px',
                    }}>
                      {value}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── Toast absoluto ── */}
        {toastMsg && (
          <div style={{
            position: 'absolute', bottom: -24, left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--teal, #2dd4bf)', color: '#0f172a',
            fontSize: 12, fontWeight: 700,
            fontFamily: "'Space Grotesk', sans-serif",
            padding: '4px 14px', borderRadius: 20,
            pointerEvents: 'none', whiteSpace: 'nowrap',
          }}>
            {toastMsg}
          </div>
        )}

        {/* ── Footer técnico ── */}
        <p style={{
          margin: 0, fontSize: 11, color: 'var(--fg3, #71717a)',
          fontFamily: "'Space Grotesk', sans-serif",
          borderTop: '1px solid var(--border, #27272a)', paddingTop: 10,
          textAlign: 'center',
        }}>
          Salida: ETRS89 · UTM zona 30N · EPSG:25830
        </p>
      </div>
    </div>
  );
};

export default CoordConversorModal;
